import { connect, useDispatch } from 'react-redux';
import { Form, FormRenderProps } from 'react-final-form';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dispatch } from 'redux';
import arrayMutators from 'final-form-arrays';

import {
  getPublishRulesActiveTreeItem,
  getPublishRulesFormData,
  getPublishRulesFormRef,
  getPublishRulesHistoryChanges,
  getPublishRulesTree,
} from 'PublishRulesPackage/MainModule/selectors/publishRulesSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { getCatalogKnmStatuses248SelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmStatusSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmStatusSelectOption';
import { IPublishRules } from 'APIPackage/PublishRulesModule/interfaces/IPublishRules';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';

import {
  createOrUpdatePublishRules,
  deletePublishRules,
  loadPublishRulesData,
  setPublishRulesFormRef,
} from '../actions/publishRulesActions';
import { PUBLISH_RULES_FORM_ID } from '../constants/publishRulesFieldsElementsIds';
import { IPublishRulesTreeItem } from '../interfaces/IPublishRulesTreeItem';
import { PublishRulesFormLeavingGuard } from './PublishRulesFormLeavingGuard';
import { IPublishRulesKnmUnscheduledForm } from '../interfaces/IPublishRulesKnmUnscheduledForm';
import { PublishRulesKnmUnscheduledFormBlock } from './PublishRulesKnmUnsheduledFormBlock';
import { PublishRulesKnmUnscheduledFormBlockNamesEnum } from '../enums/publishRulesKnmUnscheduledFormBlockNamesEnum';
import { IPublishRulesKnmUnscheduledFormRef } from '../interfaces/IPublishRulesKnmUnscheduledFormRef';
import { makeBlockTitle } from '../utils/makeBlockTitle';
import { getPublishRulesKnmUnscheduledFormInitialValues } from '../common/getPublishRulesKnmUnscheduledFormInitialValues';
import { getKnmReasonType } from '../utils/getKnmReasonType';
import { makeUnplannedValues } from '../common/makeUnplannedValues';
import { PublishRulesApproveTypeEnum } from '../enums/publishRulesApproveTypeEnum';
import { MultipleHistoryChanges } from '../reducers/publishRulesReducer';

interface IPublishRulesKnmUnscheduledFormProps {
  activeTreeItem: IPublishRulesTreeItem;
  tree: IPublishRulesTreeItem[];
  formRef?: IPublishRulesKnmUnscheduledFormRef;
  formData?: IPublishRules;
  historyChanges?: MultipleHistoryChanges;
  knmStatusesSelectOptions?: IKnmStatusSelectOption[];
}

export type IPublishRulesKnmUnscheduledFormRenderProps = FormRenderProps<IPublishRulesKnmUnscheduledForm>;

const PublishRulesKnmUnscheduledFormWithoutStore = ({
  activeTreeItem,
  tree,
  formRef,
  formData,
  historyChanges,
  knmStatusesSelectOptions,
}: IPublishRulesKnmUnscheduledFormProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [isConfirmDeleteSettingsVisible, setIsConfirmDeleteSettingsVisible] = useState<boolean>(false);
  const [reasonTypesSelectOptions, setReasonTypesSelectOptions] = useState<ISelectOption[]>([]);
  const [allowedKnmStatusesSelectOptions, setAllowedKnmStatusesSelectOptions] = useState<ISelectOption[]>([]);

  const setFormRef = useCallback(
    (newFormRef?: IPublishRulesKnmUnscheduledFormRef) => dispatch(setPublishRulesFormRef(newFormRef)),
    [],
  );

  // Значения по умолчанию
  const initialValues = useMemo(
    () => getPublishRulesKnmUnscheduledFormInitialValues(activeTreeItem, knmStatusesSelectOptions),
    [activeTreeItem.category, knmStatusesSelectOptions],
  );

  // Загрузка данных
  useEffect(() => {
    dispatch(loadPublishRulesData(activeTreeItem.category));
  }, [activeTreeItem.category]);

  // Инициализация полученными данными
  useEffect(() => {
    if (formData && formRef && knmStatusesSelectOptions) {
      const { entries, ervkKnmReasonTypes, allowedStatusCodes } = formData;

      const reasonTypesSelectOptionsFromData = ervkKnmReasonTypes?.map(item => getKnmReasonType(item));
      const allowedStatusesSelectOptions = knmStatusesSelectOptions.filter(item =>
        allowedStatusCodes?.includes(item.code),
      );

      reasonTypesSelectOptionsFromData && setReasonTypesSelectOptions(reasonTypesSelectOptionsFromData);
      allowedStatusesSelectOptions && setAllowedKnmStatusesSelectOptions(allowedStatusesSelectOptions);

      if (entries?.length !== undefined && entries?.length > 0) {
        const entryRequired =
          entries?.find(item => item.approveType === PublishRulesApproveTypeEnum.REQUIRED) || entries?.[0];
        const entryNonRequired =
          entries?.find(item => item.approveType === PublishRulesApproveTypeEnum.NON_REQUIRED) || entries?.[1];
        const entryPromptly =
          entries?.find(item => item.approveType === PublishRulesApproveTypeEnum.PROMPTLY) || entries?.[2];

        const data: IPublishRulesKnmUnscheduledForm = {
          required: makeUnplannedValues(
            entryRequired,
            knmStatusesSelectOptions,
            reasonTypesSelectOptionsFromData || [],
          ),
          nonRequired: makeUnplannedValues(
            entryNonRequired,
            knmStatusesSelectOptions,
            reasonTypesSelectOptionsFromData || [],
          ),
          promptly: makeUnplannedValues(
            entryPromptly,
            knmStatusesSelectOptions,
            reasonTypesSelectOptionsFromData || [],
          ),
        };

        formRef.current.initialize(data);
      }
    }
  }, [formData, formRef, knmStatusesSelectOptions]);

  const blockTitle = useMemo(() => makeBlockTitle(tree, activeTreeItem), [tree, activeTreeItem]);

  const handleFormSubmit = useCallback(
    (data: IPublishRulesKnmUnscheduledForm) => {
      const newData: IPublishRules = {
        entries: Object.entries(PublishRulesKnmUnscheduledFormBlockNamesEnum).map(([key, block]) => ({
          approveType: PublishRulesApproveTypeEnum[key as keyof typeof PublishRulesApproveTypeEnum],
          publicationCategoryCode: activeTreeItem.category,
          settings: {
            mainRule: {
              passportStatusCodes: data?.[block]?.uponAttainingStatus?.map(item => item.value) || null,
              publicationMode: data?.[block]?.publishDeadline?.value || null,
            },
            additionalRules:
              data?.[block]?.additionalSettings?.map(setting => ({
                reasonTypeId: (setting.typeOfBasis?.id as number) || null,
                rule: {
                  passportStatusCodes: setting.uponAttainingStatus?.map(status => status.value) || null,
                  publicationMode: setting.publishDeadline?.value || null,
                },
              })) || null,
          },
        })),
      };

      dispatch(createOrUpdatePublishRules(activeTreeItem.category, newData));
    },
    [activeTreeItem.category],
  );

  const handleApplyDeleteSetting = useCallback(() => {
    dispatch(deletePublishRules(activeTreeItem.category, initialValues));
  }, [formRef, activeTreeItem.category, initialValues]);

  const handleOnClearSetting = useCallback(() => {
    setIsConfirmDeleteSettingsVisible(true);
  }, []);

  const renderForm = ({ handleSubmit, form }: FormRenderProps<IPublishRulesKnmUnscheduledForm>) => {
    const currentFormRef = useRef<IPublishRulesKnmUnscheduledFormRenderProps['form']>(form);

    useEffect(() => {
      if (currentFormRef) {
        setFormRef(currentFormRef);
      }
    }, [setFormRef, currentFormRef]);

    return (
      <form id={PUBLISH_RULES_FORM_ID} onSubmit={handleSubmit}>
        <PublishRulesKnmUnscheduledFormBlock
          blockName={PublishRulesKnmUnscheduledFormBlockNamesEnum.REQUIRED}
          blockTitle={blockTitle}
          blockSubtitle="Требует согласования"
          isHaveSavedSettings={activeTreeItem.haveSavedSettings}
          onClose={handleOnClearSetting}
          knmStatusesSelectOptions={allowedKnmStatusesSelectOptions}
          reasonTypesSelectOptions={reasonTypesSelectOptions}
          historyChanges={
            historyChanges?.find(item => item.approveType === PublishRulesApproveTypeEnum.REQUIRED)?.changes || []
          }
        />
        <PublishRulesKnmUnscheduledFormBlock
          blockName={PublishRulesKnmUnscheduledFormBlockNamesEnum.NON_REQUIRED}
          blockTitle={blockTitle}
          blockSubtitle="Не требует согласования"
          isHaveSavedSettings={activeTreeItem.haveSavedSettings}
          knmStatusesSelectOptions={allowedKnmStatusesSelectOptions}
          reasonTypesSelectOptions={reasonTypesSelectOptions}
          historyChanges={
            historyChanges?.find(item => item.approveType === PublishRulesApproveTypeEnum.NON_REQUIRED)?.changes || []
          }
        />
        <PublishRulesKnmUnscheduledFormBlock
          blockName={PublishRulesKnmUnscheduledFormBlockNamesEnum.PROMPTLY}
          blockTitle={blockTitle}
          blockSubtitle="Незамедлительное КНМ с уведомлением прокурора"
          isHaveSavedSettings={activeTreeItem.haveSavedSettings}
          knmStatusesSelectOptions={allowedKnmStatusesSelectOptions}
          reasonTypesSelectOptions={reasonTypesSelectOptions}
          historyChanges={
            historyChanges?.find(item => item.approveType === PublishRulesApproveTypeEnum.PROMPTLY)?.changes || []
          }
        />
        <PublishRulesFormLeavingGuard formRef={formRef} />
      </form>
    );
  };

  return (
    <>
      <Form<IPublishRulesKnmUnscheduledForm>
        onSubmit={handleFormSubmit}
        render={renderForm}
        mutators={{ ...arrayMutators }}
        initialValues={initialValues}
      />
      <ConfirmModal
        visible={isConfirmDeleteSettingsVisible}
        title="Подтверждение"
        text="Удалить текущие настройки?"
        onApply={handleApplyDeleteSetting}
        onClose={() => setIsConfirmDeleteSettingsVisible(false)}
      />
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  activeTreeItem: getPublishRulesActiveTreeItem(state),
  tree: getPublishRulesTree(state),
  formRef: getPublishRulesFormRef(state),
  formData: getPublishRulesFormData(state),
  historyChanges: getPublishRulesHistoryChanges(state),
  knmStatusesSelectOptions: getCatalogKnmStatuses248SelectOptions(state),
});

export const PublishRulesKnmUnscheduledForm = connect(mapStateToProps)(PublishRulesKnmUnscheduledFormWithoutStore);
